import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import Seo, { absoluteUrl } from "../components/Seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowUpRight, ShieldCheck, KeyRound, Sparkles, ScanEye, GitBranch, Layers } from "lucide-react";

const CHAIN_ICONS = [ShieldCheck, KeyRound, Sparkles, ScanEye, GitBranch, Layers];

export default function Landing() {
    const { t, lang } = useI18n();

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                name: "Ariovis",
                url: "https://ariovis.fr",
                logo: "https://brand.ariovis.fr/logos/Logotype_BLACK.svg",
                sameAs: ["https://brand.ariovis.fr"],
                description:
                    lang === "fr"
                        ? "Pure-player européen IAM, IAG, Access Management, autorisation et PAM."
                        : "European IAM, IAG, Access Management, authorization and PAM pure-player.",
            },
            {
                "@type": "WebSite",
                name: "Ma Roadmap PAM · Ariovis",
                url: absoluteUrl(`/${lang}`),
                inLanguage: lang,
                publisher: { "@type": "Organization", name: "Ariovis" },
            },
            {
                "@type": "WebApplication",
                name: "Ma Roadmap PAM",
                applicationCategory: "SecurityApplication",
                operatingSystem: "Web",
                offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
                description:
                    lang === "fr"
                        ? "Diagnostic gratuit de maturité PAM et feuille de route personnalisée."
                        : "Free PAM maturity diagnostic and personalized roadmap.",
            },
            {
                "@type": "FAQPage",
                mainEntity: t("landing.faq_items").map((it) => ({
                    "@type": "Question",
                    name: it.q,
                    acceptedAnswer: { "@type": "Answer", text: it.a },
                })),
            },
        ],
    };

    return (
        <>
            <Seo
                title={
                    lang === "fr"
                        ? "Ma Roadmap PAM — Diagnostic de maturité gratuit | Ariovis"
                        : "Ma Roadmap PAM — Free maturity diagnostic | Ariovis"
                }
                description={
                    lang === "fr"
                        ? "Diagnostic PAM gratuit en 7 minutes : évaluez votre maturité et recevez votre feuille de route personnalisée pour sécuriser vos accès privilégiés."
                        : "Free 7-minute PAM diagnostic: assess your maturity and get a personalized roadmap to secure your privileged access."
                }
                path={`/${lang}`}
                lang={lang}
                jsonLd={jsonLd}
            />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-slate-200" data-testid="hero">
                <div className="hero-motif absolute inset-0" aria-hidden />
                <div className="relative mx-auto grid max-w-7xl gap-16 px-4 py-20 sm:px-6 md:grid-cols-12 md:py-28 lg:px-8">
                    <div className="md:col-span-8">
                        <div
                            className="inline-flex items-center gap-2 rounded-sm border border-ariovis-bordeaux/40 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-ariovis-bordeaux"
                            data-testid="hero-badge"
                        >
                            {t("landing.eyebrow")}
                        </div>
                        <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                            {t("landing.h1_a")}
                            <br />
                            <span className="brand-gradient-text">{t("landing.h1_b")}</span>
                            <br />
                            {t("landing.h1_c")}
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-slate-700 sm:text-xl">
                            {t("landing.subtitle")}
                        </p>
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <Button
                                asChild
                                size="lg"
                                className="rounded-sm bg-ariovis-bordeaux px-8 py-6 text-base text-white hover:bg-ariovis-fuschia"
                                data-testid="hero-cta-primary"
                            >
                                <Link to={`/${lang}/wizard`} className="inline-flex items-center gap-2">
                                    {t("landing.cta_primary")}
                                    <ArrowUpRight className="h-4 w-4" aria-hidden />
                                </Link>
                            </Button>
                            <a
                                href="#method"
                                className="text-sm font-semibold text-slate-700 hover:text-ariovis-bordeaux"
                                data-testid="hero-cta-secondary"
                            >
                                {t("landing.cta_secondary")} →
                            </a>
                        </div>
                        <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                            {t("landing.reassurance").map((item, idx) => (
                                <li key={idx}>· {item}</li>
                            ))}
                        </ul>
                    </div>
                    <aside className="md:col-span-4 flex flex-col justify-end">
                        <div className="rounded-md border border-slate-200 bg-white p-6 brand-shadow" data-testid="hero-preview-card">
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                {lang === "fr" ? "Aperçu du rapport" : "Report preview"}
                            </div>
                            <div className="mt-4 space-y-4">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400">
                                        {lang === "fr" ? "Maturité actuelle" : "Current maturity"}
                                    </div>
                                    <div className="mt-1 text-2xl font-semibold text-slate-950">
                                        N2 · {lang === "fr" ? "Protégé" : "Protected"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400">
                                        {lang === "fr" ? "Cible réaliste" : "Realistic target"}
                                    </div>
                                    <div className="mt-1 text-2xl font-semibold text-ariovis-bordeaux">
                                        N4 · {lang === "fr" ? "Dynamique" : "Dynamic"}
                                    </div>
                                </div>
                                <div className="rounded-sm bg-slate-950 p-4 text-sm text-slate-200">
                                    {lang === "fr"
                                        ? "Votre trajectoire commencerait par la gouvernance des habilitations privilégiées et l'harmonisation du MFA sur les consoles d'administration."
                                        : "Your trajectory would start with governance of privileged entitlements and MFA harmonization on administrative consoles."}
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                    <div className="h-full w-[42%] brand-gradient" />
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                                    {lang === "fr" ? "Données d'illustration" : "Illustrative data"}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Manifest / 5 questions */}
            <section className="relative overflow-hidden bg-slate-950 text-slate-50">
                <div className="section-motif absolute inset-0" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                    <div className="grid gap-12 md:grid-cols-12">
                        <div className="md:col-span-5">
                            <div className="text-xs font-semibold uppercase tracking-[0.32em] text-ariovis-pink">
                                {lang === "fr" ? "Manifeste" : "Manifesto"}
                            </div>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl">
                                {t("landing.manifest_title")}
                            </h2>
                            <p className="mt-6 max-w-md text-slate-300">{t("landing.manifest_intro")}</p>
                        </div>
                        <div className="md:col-span-7">
                            <ol className="space-y-6">
                                {t("landing.manifest_questions").map((q, idx) => (
                                    <li key={idx} className="border-l-2 border-ariovis-fuschia pl-6" data-testid={`manifest-q-${idx}`}>
                                        <div className="text-xs font-semibold uppercase tracking-widest text-ariovis-pink">
                                            0{idx + 1}
                                        </div>
                                        <div className="mt-1 text-lg font-semibold text-white">{q.k}</div>
                                        <p className="mt-1 text-sm text-slate-400">{q.d}</p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            {/* Chain of responsibilities (NO tech names) */}
            <section className="border-b border-slate-200 bg-white py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-[0.32em] text-ariovis-bordeaux">
                            {lang === "fr" ? "Chaîne de responsabilités" : "Chain of responsibilities"}
                        </div>
                        <h2 className="mt-4 font-display text-3xl font-semibold text-slate-950 sm:text-4xl">
                            {t("landing.chain_title")}
                        </h2>
                        <p className="mt-6 text-slate-700">{t("landing.chain_intro")}</p>
                    </div>
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {t("landing.chain").map((c, idx) => {
                            const Icon = CHAIN_ICONS[idx] || ShieldCheck;
                            const isLast = idx === t("landing.chain").length - 1;
                            return (
                                <article
                                    key={idx}
                                    className={`group rounded-md border border-slate-200 bg-white p-8 transition-colors hover:border-ariovis-fuschia ${
                                        isLast ? "md:col-span-2 lg:col-span-1 border-ariovis-bordeaux" : ""
                                    }`}
                                    data-testid={`chain-card-${idx}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className={`rounded-sm p-2 ${isLast ? "bg-ariovis-bordeaux text-white" : "bg-slate-100 text-slate-700 group-hover:bg-ariovis-fuschia group-hover:text-white"}`}>
                                            <Icon className="h-5 w-5" aria-hidden />
                                        </div>
                                        <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                                            0{idx + 1}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold text-slate-950">{c.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Method */}
            <section id="method" className="border-b border-slate-200 bg-slate-50 py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-[0.32em] text-ariovis-bordeaux">
                            {lang === "fr" ? "Méthode" : "Method"}
                        </div>
                        <h2 className="mt-4 font-display text-3xl font-semibold text-slate-950 sm:text-4xl">
                            {t("landing.method_title")}
                        </h2>
                        <p className="mt-6 text-slate-700">{t("landing.method_intro")}</p>
                    </div>
                    <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-5">
                        {t("landing.method_steps").map((s, idx) => (
                            <div
                                key={idx}
                                className="relative rounded-md border border-slate-200 bg-white p-6"
                                data-testid={`method-step-${idx}`}
                            >
                                <div className="text-xs font-semibold uppercase tracking-widest text-ariovis-fuschia">
                                    0{idx + 1}
                                </div>
                                <div className="mt-2 text-lg font-semibold text-slate-950">{s.name}</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-14 text-center">
                        <Button
                            asChild
                            size="lg"
                            className="rounded-sm bg-slate-950 px-8 py-6 text-white hover:bg-ariovis-bordeaux"
                            data-testid="method-cta"
                        >
                            <Link to={`/${lang}/wizard`}>{t("landing.cta_primary")}</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="bg-white py-20">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-ariovis-bordeaux">
                        {lang === "fr" ? "Questions fréquentes" : "Frequent questions"}
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-semibold text-slate-950 sm:text-4xl">
                        {t("landing.faq_title")}
                    </h2>
                    <div className="mt-10">
                        <Accordion type="single" collapsible className="border-t border-slate-200" data-testid="faq-accordion">
                            {t("landing.faq_items").map((item, idx) => (
                                <AccordionItem key={idx} value={`item-${idx}`} className="border-b border-slate-200">
                                    <AccordionTrigger className="text-left text-base font-semibold text-slate-950 hover:no-underline">
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm leading-relaxed text-slate-700">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* Central message CTA banner */}
            <section className="brand-gradient text-white">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-16 sm:px-6 md:flex-row md:items-center lg:px-8">
                    <p className="max-w-3xl text-xl font-semibold sm:text-2xl">
                        « {t("landing.central_message")} »
                    </p>
                    <Button
                        asChild
                        size="lg"
                        className="shrink-0 rounded-sm bg-white px-8 py-6 text-slate-950 hover:bg-slate-100"
                        data-testid="cta-banner-btn"
                    >
                        <Link to={`/${lang}/wizard`}>{t("landing.cta_primary")}</Link>
                    </Button>
                </div>
            </section>
        </>
    );
}
